import { TextFieldVariants } from '@mui/material';
import { Select, SelectProps } from 'mui-rff';
import { useField } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';

type IProps = {
  variant: TextFieldVariants;
  name: string;
} & SelectProps

const useStyles = makeStyles()((theme) => ({
  outlinedSelect: {
    '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
      borderRadius: '12px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },
    },
  },
  helperTextExpanded: {
    display: 'block',
    overflow: 'visible',
    marginTop: theme.spacing(0.375),
    maxHeight: theme.spacing(6),
    opacity: 1,
    transition: 'max-height 240ms ease, margin-top 240ms ease, opacity 240ms ease',
  },
  helperTextCollapsed: {
    display: 'block',
    overflow: 'hidden',
    marginTop: 0,
    maxHeight: '0px',
    opacity: 0,
    transition: 'max-height 240ms ease, margin-top 240ms ease, opacity 240ms ease',
  },
}))

export default function FormControlledSelect({
  variant,
  name,
  formControlProps,
  formHelperTextProps,
  data,
  helperText,
  showError,
  error,
  ...props
}: IProps) {
  const { classes } = useStyles();

  const field = useField(name, {
    subscription: {
      error: true,
      submitError: true,
      dirtySinceLastSubmit: true,
      touched: true,
      modified: true,
    },
  });

  const computedShowError = showError
    ? showError({ meta: field.meta })
    : !!(((field.meta.submitError && !field.meta.dirtySinceLastSubmit) || field.meta.error) && (field.meta.touched || field.meta.modified));

  const computedErrorText = computedShowError
    ? (field.meta.error || field.meta.submitError)
    : helperText;

  const resolvedError = error ?? computedShowError;

  const mergedFormHelperTextProps = {
    ...formHelperTextProps,
    error: resolvedError,
    sx: {
      mt: 0,
      minHeight: 0,
      overflow: 'visible',
      ...((formHelperTextProps as { sx?: Record<string, unknown> } | undefined)?.sx || {}),
    },
  };

  const helperTextClassName = computedErrorText
    ? classes.helperTextExpanded
    : classes.helperTextCollapsed;

  const animatedHelperText = <span className={helperTextClassName}>{computedErrorText || null}</span>;

  return (
    <>
      <Select
        {...props}
        className={classes.outlinedSelect}
        color="primary"
        data={data}
        error={resolvedError}
        formControlProps={{ ...formControlProps, error: resolvedError }}
        formHelperTextProps={mergedFormHelperTextProps}
        helperText={animatedHelperText}
        name={name}
        showError={() => false}
        variant={variant}
      />
    </>
  )
}